import React, { Fragment } from 'react';
import T from 'prop-types';
import { isEmpty } from 'lodash';
import classnames from 'classnames';

import { getAuthCredentials } from 'common/util/auth-storage';
import { SigningIframe } from 'part11/components/SigningIframe/SigningIframe';
import { Spinner } from 'components/common/LoadingStatus/LoadingStatus';
import { TaskShape } from 'workflow/constants/newInbox';
import { useHideNavBar, useHideNavBarV4 } from 'workflow/utils/useHideNavBar';
import { BackToInbox, BackToMyTasksV4 } from 'workflow/components/NewInbox/Common/BackToInbox/BackToInbox';
import Layout from 'workflow/components/NewInbox/Common/Layout/Layout';
import { openPopup, listenForCredentials } from 'common/util/popup';
import { fetchSignUrl } from 'common/actions/login';

import styles from './TaskSignature.scss';

const TASK_ACTION_KEYS = {
    APPROVE_AND_SIGN: 'approve_and_sign',
    CERTIFY_AND_SIGN: 'certify_and_sign',
    REVIEW_AND_SIGN: 'review_and_sign',
    TRAIN_AND_SIGN: 'train_and_sign',
    READ_AND_ACKNOWLEDGE: 'read_and_acknowledge',
};

const ACTION_KEY_TO_LABEL_MAPPING = {
    [TASK_ACTION_KEYS.APPROVE_AND_SIGN]: 'Approvals',
    [TASK_ACTION_KEYS.CERTIFY_AND_SIGN]: 'Certified Copy',
    [TASK_ACTION_KEYS.REVIEW_AND_SIGN]: 'Reviews',
    [TASK_ACTION_KEYS.TRAIN_AND_SIGN]: 'Trainings',
};

const groupTasksByActionKey = tasks => {
    const result = [];
    const actionKeys = Object.keys(ACTION_KEY_TO_LABEL_MAPPING);
    actionKeys.forEach(actionKey => {
        const filteredTasks = tasks.filter(task => task.actionKey === actionKey);
        if (filteredTasks.length) {
            result.push({
                key: actionKey,
                label: ACTION_KEY_TO_LABEL_MAPPING[actionKey],
                tasks: filteredTasks,
            });
        }
    });
    return result;
};

const getSigningUrl = (originUrl, actionText, ssosign = false) => {
    const accessToken = getAuthCredentials().accessToken;
    let url = `${originUrl}/sign?access_token=${accessToken}&action_text=${actionText}`;
    if (ssosign) {
        url += '&ssosign=true';
    }
    return url;
};

const TaskSignature = ({
    tasksSigning,
    tasksForMultiSign,
    taskForSingleSign,
    changeView,
    headerClass,
    bodyClass,
    originUrl,
    actionText,
    onSignTasksSubmit,
    isNewV4 = false,
    isMobileView = false,
    callBackOnSuccess = () => { },
    addApplicationMessage,
    isSSOSigningEnabled = false,
    isSsoUser = false
}) => {
    const tasksForSignature = isEmpty(taskForSingleSign) ? tasksForMultiSign : [taskForSingleSign];
    const tasksGroupedByActionKey = groupTasksByActionKey(tasksForSignature);
    
    const accessToken = getAuthCredentials().accessToken;
    // Iframe URL should show the form
    const iframeUrl = getSigningUrl(originUrl, actionText);

    if (!isMobileView) {
        if (isNewV4) {
            useHideNavBarV4();
        } else {
            useHideNavBar();
        }
    }

    const signDocumentHandler = token => {
        onSignTasksSubmit(tasksForSignature, token, isNewV4, callBackOnSuccess);
    };

    const body = (
        <div className={styles.TaskSignature} data-cy="TaskSignature">
            <div className={classnames(styles.TaskSignature__Header, headerClass)}>
                {isNewV4 ? <BackToMyTasksV4 changeView={changeView} /> : <BackToInbox changeView={changeView} />}
                <div className={styles.TaskSignature__DesktopAndTabletPageTitle}>Signature Queue</div>
                <div className={styles.TaskSignature__HeaderRightPlaceholder} />
            </div>

            <div className={classnames(styles.TaskSignature__Body, bodyClass)}>
                <div className={styles.TaskSignature__MobilePageTitle}>Signature Queue</div>

                {tasksSigning ? (
                    <div className={styles.TaskSignature__Loader}>
                        <Spinner actionLabel={'Completing'} itemName={'Tasks'} />
                    </div>
                ) : (
                    <Fragment data-cy="TaskSignature-Fragment">
                        {tasksGroupedByActionKey.map(item => {
                            return (
                                <Fragment key={item.key} data-cy={'TaskSignature-' + item.key}>
                                    <div className={styles.TaskSignature__SectionHeader} data-cy={'label-TaskSignature-' + item.key}>{item.label}</div>
                                    {item.tasks.map(task => (
                                        <div key={task.id} className={styles.TaskSignature__Task} data-cy={task.id - 'label-TaskSignature-' + item.key}>
                                            <div className={styles.TaskSignature__TaskTitle}>{task.title}</div>
                                            <div className={styles.TaskSignature__TaskLabel}>STATEMENT OF TESTAMENT</div>
                                            <div className={styles.TaskSignature__TaskStatement}>{task.statement}</div>
                                        </div>
                                    ))}
                                </Fragment>
                            );
                        })}
                        {isSSOSigningEnabled && isSsoUser ? (
                            <div className={styles.TaskSignature__SSOWrapper}>
                                <button className={styles.TaskSignature__SSOButton} onClick={() => {
                                    fetchSignUrl(accessToken, true, originUrl).then(res => {
                                        const popup = openPopup(res.provider_uri, 'sign');
                                        listenForCredentials(popup)
                                            .then(res => signDocumentHandler(res.token))
                                            .catch(err => addApplicationMessage('error', err.message || 'SSO authentication failed.'));
                                    });
                                }}>
                                    Authenticate with SSO to Sign
                                </button>
                            </div>
                        ) : (
                            <div className={styles.TaskSignature__IFrameWrapper}>
                                <SigningIframe width="400" height="320" url={iframeUrl} signDocument={signDocumentHandler} />
                            </div>
                        )}
                    </Fragment>
                )}
            </div>
        </div>
    );

    const footer = <div />;

    return <Layout body={body} footer={footer} />;
};

TaskSignature.propTypes = {
    tasksSigning: T.bool,
    tasksForMultiSign: T.arrayOf(T.shape(TaskShape)),
    setTaskForSingleSign: T.func,
    changeView: T.func,
    headerClass: T.string,
    bodyClass: T.string,
    originUrl: T.string,
    actionText: T.string,
    onSignTasksSubmit: T.func,
    addApplicationMessage: T.func,
};

export { groupTasksByActionKey, ACTION_KEY_TO_LABEL_MAPPING, TASK_ACTION_KEYS };
export default TaskSignature;
